import React, { useEffect, useState } from 'react';
import { Grid, Typography, Box, Divider, ButtonProps } from '@mui/material';
import ButtonGeneral from '../general/buttons/buttonGeneral';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import {
    DataGrid,
    GridToolbarContainer,
    esES,
    GridToolbarContainerProps,
    GridToolbarExportContainer,
    GridCsvExportOptions,
    GridCsvExportMenuItem,
    GridPrintExportMenuItem,
} from '@mui/x-data-grid';
import { useAuthContext } from '../../../contexts/authContext';

type Props = {
    title: string;
    titleButtonAdd?: string;
    startIconButtonAdd?: JSX.Element | string;
    onClickAdd?: any;
    onClickBack: () => void;
    columns?: any;
    rows?: any;
    rowCount?: number;
    loading?: boolean;
    checkboxSelection?: boolean | false;
    onSetPage?: any;
    disableRow?: boolean | false;
    disableAdd?: boolean | false;
    enabledSearch?: boolean | false;
    searchField?: JSX.Element | string;
    disableDataGrid?: false | boolean;
    enabledExport?: false | boolean;
    nameExport?: string;
};

const ListViewAll = ({
    title,
    titleButtonAdd,
    startIconButtonAdd,
    onClickAdd,
    onClickBack,
    columns,
    rows,
    rowCount,
    checkboxSelection,
    disableRow,
    disableAdd,
    disableDataGrid,
    onSetPage,
    loading,
    enabledSearch,
    searchField,
    enabledExport,
    nameExport,
}: Props) => {
    const { modeTheme } = useAuthContext();
    const csvOptions: GridCsvExportOptions = {
        delimiter: ';',
        fileName: nameExport || '',
    };
    const [filterActive, setFilterActive] = useState<boolean>(false);
    const [paginationModel, setPaginationModel] = useState({
        pageSize: 20,
        page: 0,
    });

    useEffect(() => {
        if (!disableDataGrid) {
            onSetPage(paginationModel);
        }
    }, [disableDataGrid, onSetPage, paginationModel, loading, rows]);

    function CustomExportButton(props: ButtonProps) {
        return (
            <GridToolbarExportContainer {...props}>
                <GridCsvExportMenuItem options={csvOptions} />
                <GridPrintExportMenuItem />
            </GridToolbarExportContainer>
        );
    }

    function CustomToolbar(props: GridToolbarContainerProps) {
        return (
            <GridToolbarContainer {...props}>
                <CustomExportButton />
            </GridToolbarContainer>
        );
    }

    return (
        <Box px={2} sx={{ minWidth: 380 }}>
            <Typography
                component="h3"
                px={1}
                sx={{
                    flexGrow: 1,
                    color:
                        modeTheme === 'darkMode'
                            ? 'primary.contrastText'
                            : 'primary.main',
                    fontSize: { lg: 24, md: 22, xs: 20 },
                }}
            >
                {title}
            </Typography>
            <Divider />
            <Grid container mt={2}>
                {enabledSearch && (
                    <Grid item xs={12} sm={5} md={5} lg={5}>
                        {searchField}
                    </Grid>
                )}
                <Grid
                    item
                    xs={12}
                    sm={enabledSearch ? 7 : 12}
                    md={enabledSearch ? 7 : 12}
                    lg={enabledSearch ? 7 : 12}
                    sx={{
                        mt: {
                            xs: enabledSearch ? 2 : 0,
                            sm: 0,
                        },
                        textAlign: 'right',
                    }}
                >
                    {disableAdd !== true ? (
                        <ButtonGeneral
                            onClick={onClickAdd}
                            startIcon={startIconButtonAdd || undefined}
                            title={titleButtonAdd || ''}
                            sx={{
                                mr: 1,
                                backgroundColor:
                                    modeTheme === 'darkMode'
                                        ? 'primary.contrastText'
                                        : 'primary.main',
                            }}
                        />
                    ) : null}
                    <ButtonGeneral
                        onClick={onClickBack}
                        startIcon={
                            <ArrowBackIcon
                                sx={{
                                    color:
                                        modeTheme === 'darkMode'
                                            ? 'primary.main'
                                            : 'primary.contrastText',
                                }}
                            />
                        }
                        title="Volver"
                        sx={{
                            fontSize: { lg: 12, md: 10.5, xs: 9 },
                            backgroundColor:
                                modeTheme === 'darkMode'
                                    ? 'primary.contrastText'
                                    : 'primary.main',
                        }}
                    />
                </Grid>
            </Grid>
            {!disableDataGrid ? (
                <Box mt={2.5}>
                    <DataGrid
                        localeText={
                            esES.components.MuiDataGrid.defaultProps.localeText
                        }
                        rows={rows || []}
                        columns={columns}
                        autoHeight
                        paginationModel={paginationModel}
                        rowCount={filterActive ? undefined : rowCount}
                        paginationMode="server"
                        loading={loading}
                        pageSizeOptions={[20, 50, 100]}
                        onPaginationModelChange={setPaginationModel}
                        checkboxSelection={checkboxSelection}
                        disableRowSelectionOnClick={disableRow}
                        onFilterModelChange={(model) =>
                            setFilterActive(
                                Boolean(model?.items[0]?.value !== undefined)
                            )
                        }
                        slots={{
                            toolbar: enabledExport ? CustomToolbar : undefined,
                        }}
                        sx={{
                            '.MuiTablePagination-displayedRows': {
                                marginTop: '1rem',
                            },
                            '.MuiTablePagination-selectLabel': {
                                marginTop: '1rem',
                            },
                            '.MuiDataGrid-columnHeaders': {
                                color:
                                    modeTheme === 'darkMode'
                                        ? 'primary.main'
                                        : 'primary.contrastText',
                                backgroundColor:
                                    modeTheme === 'darkMode'
                                        ? 'primary.contrastText'
                                        : 'primary.main',
                            },
                            '.css-1pe4mpk-MuiButtonBase-root-MuiIconButton-root':
                                {
                                    color:
                                        modeTheme === 'darkMode'
                                            ? 'primary.main'
                                            : 'primary.contrastText',
                                },
                            '.MuiDataGrid-iconSeparator': {
                                color:
                                    modeTheme === 'darkMode'
                                        ? 'primary.main'
                                        : 'primary.contrastText',
                            },
                            '.css-1i1c21g-MuiButtonBase-root-MuiButton-root': {
                                color:
                                    modeTheme === 'darkMode'
                                        ? 'primary.contrastText'
                                        : 'primary.main',
                            },
                            '.MuiCircularProgress-root': {
                                color:
                                    modeTheme === 'darkMode'
                                        ? 'primary.contrastText'
                                        : 'primary.main',
                            },
                        }}
                    />
                </Box>
            ) : null}
        </Box>
    );
};

export default ListViewAll;
